import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
    CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Icon, IconButton, InputLabel, Switch, Table,
    TableBody, TableCell, TableHead, TableRow, Tooltip,
} from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import { useMaterialUIController } from "context";
import { coresDoTema } from "assets/coresTema";
import { MySwal } from "App";
import { errorAlert } from "utils/Functions";
import { post } from "utils/WebService";

/**
 * Cadastro dos motivos de afastamento (aberto pela tela do Usuário > Afastamentos; Usuários > Editar). Inclui, renomeia e
 * inativa; exclui só o motivo que nenhum afastamento usa (o usado se inativa: sai da lista da inclusão e continua nos
 * afastamentos antigos). Cada mudança devolve a lista inteira, que volta para a tela de afastamentos por onChange.
 */
const MotivosAfastamentoDialog = ({ open, motivos, onChange, onClose }) => {
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;
    const cores = coresDoTema(darkMode);
    const [novo, setNovo] = useState("");
    // Renomeando: { id, nome }
    const [editando, setEditando] = useState(null);
    const [ocupado, setOcupado] = useState(null);

    useEffect(() => {
        if (!open) {
            setNovo("");
            setEditando(null);
        }
    }, [open]);

    const gravar = (corpo, chave, depois) => {
        setOcupado(chave);
        post("/users/afastamentos/motivos/salvar", corpo)
            .then((r) => {
                onChange(r?.motivos || []);
                if (depois) depois();
            })
            .catch((err) => errorAlert(err?.message || "Erro ao salvar o motivo."))
            .finally(() => setOcupado(null));
    };

    const excluir = (m) => {
        MySwal.fire({
            title: "Excluir o motivo?",
            text: `"${m.nome}" sai da lista. Nenhum afastamento usa este motivo.`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Excluir",
            cancelButtonText: "Cancelar",
        }).then((res) => {
            if (!res.isConfirmed) return;
            setOcupado(`excluir-${m.id}`);
            post("/users/afastamentos/motivos/excluir", { id: m.id })
                .then((r) => onChange(r?.motivos || []))
                .catch((err) => errorAlert(err?.message || "Erro ao excluir o motivo."))
                .finally(() => setOcupado(null));
        });
    };

    const celula = { fontSize: 12, color: cores.texto, verticalAlign: "top", pt: 1.5 };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Motivos de afastamento</DialogTitle>
            <DialogContent>
                <Grid container spacing={2} alignItems="flex-start" sx={{ mb: 1.5 }}>
                    <Grid item xs={12} sm={8}>
                        <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Novo motivo</InputLabel>
                        <MDInput
                            size="small"
                            fullWidth
                            value={novo}
                            inputProps={{ maxLength: 100 }}
                            placeholder="Ex.: Licença casamento"
                            onChange={(e) => setNovo(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" && novo.trim()) gravar({ nome: novo }, "novo", () => setNovo(""));
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>&nbsp;</InputLabel>
                        <MDButton
                            variant="contained"
                            color="info"
                            size="medium"
                            fullWidth
                            sx={{ height: 38 }}
                            disabled={!novo.trim() || ocupado === "novo"}
                            onClick={() => gravar({ nome: novo }, "novo", () => setNovo(""))}
                            endIcon={ocupado === "novo" ? <CircularProgress color="inherit" size={16} /> : null}
                        >
                            Incluir
                        </MDButton>
                    </Grid>
                </Grid>
                <MDTypography variant="caption" color="text" sx={{ display: "block", mb: 1 }}>
                    O motivo inativo sai da lista da inclusão e continua nos afastamentos antigos. Só dá para excluir o motivo que
                    nenhum afastamento usa.
                </MDTypography>
                <Table size="small">
                    <TableHead sx={{ display: "table-header-group" }}>
                        <TableRow>
                            {["Motivo", "Afastamentos", "Ativo", ""].map((t) => (
                                <TableCell key={t} sx={{ fontSize: 12, fontWeight: "bold", color: cores.texto, backgroundColor: cores.painel }}>{t}</TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {motivos.map((m) => {
                            const renomeando = editando?.id === m.id;
                            return (
                                <TableRow key={m.id}>
                                    <TableCell sx={celula}>
                                        {renomeando ? (
                                            <MDInput
                                                size="small"
                                                fullWidth
                                                autoFocus
                                                value={editando.nome}
                                                inputProps={{ maxLength: 100 }}
                                                onChange={(e) => setEditando({ ...editando, nome: e.target.value })}
                                                onKeyDown={(e) => {
                                                    if (e.key === "Enter" && editando.nome.trim()) {
                                                        gravar({ id: m.id, nome: editando.nome }, `nome-${m.id}`, () => setEditando(null));
                                                    }
                                                    if (e.key === "Escape") setEditando(null);
                                                }}
                                            />
                                        ) : (
                                            <MDBox component="span" sx={{ color: m.ativo ? "inherit" : cores.textoSecundario }}>
                                                {m.nome}{m.ativo ? "" : " (inativo)"}
                                            </MDBox>
                                        )}
                                    </TableCell>
                                    <TableCell sx={{ ...celula, color: cores.textoSecundario }}>{m.emUso || "-"}</TableCell>
                                    <TableCell sx={{ verticalAlign: "top", pt: 1 }}>
                                        <Switch
                                            size="small"
                                            checked={!!m.ativo}
                                            disabled={ocupado === `ativo-${m.id}`}
                                            onChange={(e) => gravar({ id: m.id, nome: m.nome, ativo: e.target.checked }, `ativo-${m.id}`)}
                                        />
                                    </TableCell>
                                    <TableCell align="right" sx={{ verticalAlign: "top", pt: 1, whiteSpace: "nowrap" }}>
                                        {renomeando ? (
                                            <>
                                                <Tooltip title="Salvar o nome">
                                                    <span>
                                                        <IconButton
                                                            size="small"
                                                            color="info"
                                                            disabled={!editando.nome.trim() || ocupado === `nome-${m.id}`}
                                                            onClick={() => gravar({ id: m.id, nome: editando.nome }, `nome-${m.id}`, () => setEditando(null))}
                                                        >
                                                            <Icon fontSize="small">check</Icon>
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                                <Tooltip title="Cancelar">
                                                    <IconButton size="small" onClick={() => setEditando(null)}>
                                                        <Icon fontSize="small">close</Icon>
                                                    </IconButton>
                                                </Tooltip>
                                            </>
                                        ) : (
                                            <>
                                                <Tooltip title="Renomear">
                                                    <IconButton size="small" color="info" onClick={() => setEditando({ id: m.id, nome: m.nome })}>
                                                        <Icon fontSize="small">edit</Icon>
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title={m.emUso ? "Em uso: inative em vez de excluir" : "Excluir"}>
                                                    <span>
                                                        <IconButton
                                                            size="small"
                                                            color="error"
                                                            disabled={!!m.emUso || ocupado === `excluir-${m.id}`}
                                                            onClick={() => excluir(m)}
                                                        >
                                                            <Icon fontSize="small">delete</Icon>
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                            </>
                                        )}
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                        {motivos.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={4} sx={{ fontSize: 12, color: cores.textoSecundario }}>Nenhum motivo cadastrado.</TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </DialogContent>
            <DialogActions>
                <MDButton variant="text" color="secondary" onClick={onClose}>Fechar</MDButton>
            </DialogActions>
        </Dialog>
    );
};

MotivosAfastamentoDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    motivos: PropTypes.arrayOf(PropTypes.object).isRequired,
    onChange: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
};

export default MotivosAfastamentoDialog;
